// ./src/components/generic/ContentBlockSelector/ContentBlockSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select } from "@mantine/core";
import { useContentBlocks } from "@/hooks/useContentBlocks";
import { ContentBlock } from "@/utils/api/ContentBlockService";

export interface ContentBlockSelectorProps {
  /** Selected content block ID */
  value: string | null;

  /** Callback when content block selection changes */
  onChange?: (blockId: string | null, block?: ContentBlock) => void;

  /** Venue ID to filter content blocks by */
  venueId?: string | null | undefined;

  /** Content type to filter by (e.g., 'text', 'image', 'video') */
  contentType?: string | undefined;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive content blocks */
  includeInactive?: boolean;

  /** Custom filter function for content blocks */
  blockFilter?: (block: ContentBlock) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;

  /** Additional props passed to Select */
  [key: string]: any;
}

export const ContentBlockSelector: React.FC<ContentBlockSelectorProps> = ({
  value,
  onChange,
  venueId,
  contentType,
  placeholder = "Select a content block",
  includeInactive = false,
  blockFilter,
  required = false,
  loadingPlaceholder = "Loading content blocks...",
  emptyText = "No content blocks available",
  error,
  disabled,
  size = "md",
  label,
  ...selectProps
}) => {
  // Use content blocks hook with venue and content type filters
  const { contentBlocks, loading, isEmpty, lastError } = useContentBlocks({
    venueId: venueId || undefined,
    contentType: contentType,
    autoLoad: true,
  });

  // Filter and prepare content block options
  const blockOptions = useMemo(() => {
    let filteredBlocks = contentBlocks;

    // Apply status filter (status 100 = active, 0 = disabled)
    if (!includeInactive) {
      filteredBlocks = filteredBlocks.filter(
        (block) => block.status === 100 // Only show active content blocks
      );
    }

    // Apply custom filter if provided
    if (blockFilter) {
      filteredBlocks = filteredBlocks.filter(blockFilter);
    }

    // Sort by orderNo first, then by name for better UX
    filteredBlocks.sort((a, b) => {
      // First sort by orderNo if available
      if (a.orderNo !== undefined && b.orderNo !== undefined) {
        if (a.orderNo !== b.orderNo) {
          return a.orderNo - b.orderNo;
        }
      }

      // Then sort by name
      const nameA = a.name || "Unnamed Block";
      const nameB = b.name || "Unnamed Block";
      return nameA.localeCompare(nameB);
    });

    // Convert to select options
    return filteredBlocks.map((block) => {
      const displayName = block.name || "Unnamed Block";
      const truncatedName =
        displayName.length > 50
          ? `${displayName.substring(0, 50)}...`
          : displayName;

      // Add content type badge to label if available
      const labelWithType = block.contentType
        ? `${truncatedName} (${block.contentType})`
        : truncatedName;

      return {
        value: block.id || "",
        label: labelWithType,
        disabled: block.status === 0, // Disabled blocks have status 0
      };
    });
  }, [contentBlocks, includeInactive, blockFilter]);

  // Create a map for quick content block lookup by ID
  const blockMap = useMemo(() => {
    const map = new Map();
    contentBlocks.forEach((block) => {
      if (block.id) {
        map.set(block.id, block);
      }
    });
    return map;
  }, [contentBlocks]);

  // Auto-select first content block if required and no value provided
  useEffect(() => {
    if (required && blockOptions.length > 0 && !loading && onChange && !value) {
      const firstBlock = blockOptions[0];
      if (firstBlock && !firstBlock.disabled) {
        const blockObject = blockMap.get(firstBlock.value);
        onChange(firstBlock.value, blockObject);
      }
    }
  }, [required, value, blockOptions, loading, onChange, blockMap]);

  // Handle onChange with content block object lookup
  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedBlock = blockMap.get(selectedValue);
      onChange?.(selectedValue, selectedBlock);
    } else {
      // When clearing selection, pass null for both ID and block
      onChange?.(null, undefined);
    }
  };

  // Show loading state
  if (loading && contentBlocks.length === 0) {
    return (
      <Select
        {...selectProps}
        label={label}
        size={size}
        data={[]}
        value={null}
        onChange={() => {}} // No-op during loading
        placeholder={loadingPlaceholder}
        disabled={true}
        error={error}
      />
    );
  }

  // Show error state if there's an API error
  if (lastError && !loading) {
    return (
      <Select
        {...selectProps}
        label={label}
        size={size}
        data={[]}
        value={null}
        onChange={() => {}} // No-op during error
        placeholder="Error loading content blocks"
        disabled={true}
        error={error || lastError}
      />
    );
  }

  // Show empty state if no content blocks are available
  if (isEmpty && !loading) {
    return (
      <Select
        {...selectProps}
        label={label}
        size={size}
        data={[]}
        value={null}
        onChange={() => {}} // No-op since there's nothing to select
        placeholder={emptyText}
        disabled={true}
        error={error}
      />
    );
  }

  // Render the select component
  return (
    <Select
      {...selectProps}
      label={label}
      size={size}
      data={blockOptions}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      clearable={!required} // clearable is opposite of required
      searchable
      required={required}
      disabled={disabled || loading}
      error={error}
    />
  );
};
